/* eslint-disable @typescript-eslint/no-deprecated */
import { z } from 'zod/v4';
import { RepeatType } from '@masters-club/types';
import { stringToBooleanSchema } from '../common/index';

// ==================== Enums as Zod Schemas ====================

export const repeatTypeSchema = z.nativeEnum(RepeatType);

// ==================== Translation Schema ====================

export const translationSchema = z.object({
  languageCode: z.string().length(2, 'Код языка должен быть 2 символа'),
  text: z.string()
    .min(1, 'Текст обязателен')
    .max(500, 'Текст слишком длинный (максимум 500 символов)'),
});

// ==================== Goal Schemas ====================

export const createGoalSchema = z.object({
  text: z.string({ message: 'Текст цели обязателен' })
    .min(1, 'Текст цели обязателен')
    .max(500, 'Текст цели слишком длинный (максимум 500 символов)'),
  isPublic: z.boolean().default(false),
  deadline: z.coerce.date({ message: 'Некорректный формат даты' }).nullable().optional(),
  translations: z.array(translationSchema).optional(),
});

export const updateGoalSchema = z.object({
  text: z.string()
    .min(1, 'Текст цели обязателен')
    .max(500, 'Текст цели слишком длинный (максимум 500 символов)')
    .optional(),
  isPublic: z.boolean().optional(),
  deadline: z.coerce.date({ message: 'Некорректный формат даты' }).nullable().optional(),
  translations: z.array(translationSchema).optional(),
});

export const goalFiltersSchema = z.object({
  isCompleted: stringToBooleanSchema,
  isPublic: stringToBooleanSchema,
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(100, 'Лимит должен быть <= 100').default(20),
});

export const publicGoalsFiltersSchema = z.object({
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(50, 'Лимит должен быть <= 50').default(20),
});

// ==================== Reminder Schemas ====================

export const createReminderSchema = z.object({
  text: z.string({ message: 'Текст напоминания обязателен' })
    .min(1, 'Текст напоминания обязателен')
    .max(500, 'Текст напоминания слишком длинный (максимум 500 символов)'),
  repeatType: repeatTypeSchema.default(RepeatType.ONCE),
  repeatDays: z.array(z.number().int('День недели должен быть целым числом').min(0, 'День недели от 0 до 6').max(6, 'День недели от 0 до 6')).default([]),
  repeatTime: z.array(z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Время должно быть в формате HH:MM')).default([]),
  endDate: z.coerce.date({ message: 'Некорректный формат даты' }).nullable().optional(),
  translations: z.array(translationSchema).optional(),
});

export const updateReminderSchema = z.object({
  text: z.string()
    .min(1, 'Текст напоминания обязателен')
    .max(500, 'Текст напоминания слишком длинный (максимум 500 символов)')
    .optional(),
  repeatType: repeatTypeSchema.optional(),
  repeatDays: z.array(z.number().int('День недели должен быть целым числом').min(0, 'День недели от 0 до 6').max(6, 'День недели от 0 до 6')).optional(),
  repeatTime: z.array(z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Время должно быть в формате HH:MM')).optional(),
  endDate: z.coerce.date({ message: 'Некорректный формат даты' }).nullable().optional(),
  translations: z.array(translationSchema).optional(),
});

export const reminderFiltersSchema = z.object({
  isCompleted: stringToBooleanSchema,
  repeatType: repeatTypeSchema.optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(100, 'Лимит должен быть <= 100').default(20),
});

// ==================== Intention Schemas ====================

export const createIntentionSchema = z.object({
  text: z.string({ message: 'Текст намерения обязателен' })
    .min(1, 'Текст намерения обязателен')
    .max(500, 'Текст намерения слишком длинный (максимум 500 символов)'),
});

export const completeIntentionSchema = z.object({
  proofUrl: z.string().url('Некорректный URL').optional(),
  comment: z.string().max(1000, 'Комментарий слишком длинный (максимум 1000 символов)').optional(),
});

export const intentionFiltersSchema = z.object({
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(100, 'Лимит должен быть <= 100').default(20),
});

export const intentionHistoryFiltersSchema = z.object({
  days: z.coerce.number().int('Количество дней должно быть целым числом').positive('Количество дней должно быть положительным').max(365, 'Максимум 365 дней').default(30),
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(100, 'Лимит должен быть <= 100').default(20),
});

// ==================== ID Param Schemas ====================

export const idParamSchema = z.object({
  id: z.coerce.number().int('ID должен быть целым числом'),
});

// ==================== Type Exports ====================

export type CreateGoalDto = z.infer<typeof createGoalSchema>;
export type UpdateGoalDto = z.infer<typeof updateGoalSchema>;
export type GoalFiltersDto = z.infer<typeof goalFiltersSchema>;
export type PublicGoalsFiltersDto = z.infer<typeof publicGoalsFiltersSchema>;

export type CreateReminderDto = z.infer<typeof createReminderSchema>;
export type UpdateReminderDto = z.infer<typeof updateReminderSchema>;
export type ReminderFiltersDto = z.infer<typeof reminderFiltersSchema>;

export type CreateIntentionDto = z.infer<typeof createIntentionSchema>;
export type CompleteIntentionDto = z.infer<typeof completeIntentionSchema>;
export type IntentionFiltersDto = z.infer<typeof intentionFiltersSchema>;
export type IntentionHistoryFiltersDto = z.infer<typeof intentionHistoryFiltersSchema>;
